import {
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import ListTable from "common/ListTable";
import {
  CNF_PREPARE_BILLING_NEXT_BILL_NUMBER_ENDPOINT,
  CNF_PREPARE_BILLING_SHOW_ENDPOINT,
  CNF_PREPARE_BILLING_STORE_ENDPOINT,
  CNF_PREPARE_BILLING_UPDATE_ENDPOINT,
  EXPORT_CNF_BILL_LIST_ENDPOINT,
  IMPORT_CNF_BILL_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "apiServices/common";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getExportCnfBillRowTotal, getImportCnfBillRowTotal } from "./cnfBillAmount";
import {
  formatAmountTaka,
  formatGoodsDescription,
  formatLcNoExport,
  formatLcNoImport,
  formatQuantityExport,
  formatQuantityImport,
  formatShortDate,
} from "./prepareBillingFormatters";

import PrepareBillingStatusTag from "./PrepareBillingStatusTag";

const { Title, Text } = Typography;

export const IMPORT_VAL_PREFIX = "import:";
export const EXPORT_VAL_PREFIX = "export:";

function mergeUniqueBills(existing, incoming) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((row) => {
    if (row?.id != null) {
      map.set(row.id, row);
    }
  });
  return Array.from(map.values());
}

function itemToSelectedKey(item) {
  if (item.bill_type === "import") {
    return `${IMPORT_VAL_PREFIX}${item.bill_id}`;
  }
  if (item.bill_type === "export") {
    return `${EXPORT_VAL_PREFIX}${item.bill_id}`;
  }
  return null;
}

export default function PrepareBillingForm({
  viewMode = false,
  isEdit = false,
  prepareBillingId,
  editInfo: editInfoProp,
  canUpdate: canUpdateProp,
  canEditApprovedAmount = false,
}) {
  const navigate = useNavigate();
  const isAdd = !isEdit && !viewMode;

  const [loadingLists, setLoadingLists] = useState(true);
  const [loadingRecord, setLoadingRecord] = useState(!!prepareBillingId);
  const [fetchError, setFetchError] = useState(null);
  const [importBills, setImportBills] = useState([]);
  const [exportBills, setExportBills] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [approvedAmounts, setApprovedAmounts] = useState({});
  const [billNumber, setBillNumber] = useState("");
  const [recordStatus, setRecordStatus] = useState(null);
  const [loadingBillNumber, setLoadingBillNumber] = useState(isAdd);
  const [saving, setSaving] = useState(false);

  const canUpdate = canUpdateProp ?? !viewMode;
  const disabled = viewMode || !canUpdate;
  const approvedAmountEditable = canEditApprovedAmount && !viewMode;

  const listPath =
    "/import-export-expense-tracking/external-cnf-bills/prepare-billing";

  const loadAvailableBills = useCallback(async () => {
    const q = "?page=1&per_page=5000";
    const listBody = {
      available_for_prepare_billing: 1,
      ...(prepareBillingId ? { prepare_billing_id: prepareBillingId } : {}),
    };
    const [importRes, exportRes] = await Promise.all([
      getData(`${IMPORT_CNF_BILL_LIST_ENDPOINT}${q}`, false, listBody),
      getData(`${EXPORT_CNF_BILL_LIST_ENDPOINT}${q}`, false, listBody),
    ]);

    let importRows = [];
    let exportRows = [];

    if (importRes?.status === 200) {
      importRows = importRes.data?.data?.data || [];
    } else {
      message.error("Could not load Import C&F bills.");
      setFetchError("Could not load this list.");
    }

    if (exportRes?.status === 200) {
      exportRows = exportRes.data?.data?.data || [];
    } else {
      message.error("Could not load Export C&F bills.");
      setFetchError("Could not load this list.");
    }

    return { importRows, exportRows };
  }, [prepareBillingId]);

  const loadRecord = useCallback(async (id) => {
    const response = await getData(`${CNF_PREPARE_BILLING_SHOW_ENDPOINT}/${id}`);
    if (response?.status !== 200 || response?.data?.code !== 200) {
      message.error("Could not load prepare billing.");
      setFetchError("Could not load this list.");
      return null;
    }
    return response.data?.data;
  }, []);

  const applyRecordToState = useCallback((record) => {
    if (!record) return;

    setBillNumber(record.bill_number || "");
    setRecordStatus(record.status || null);

    const keys = [];
    const approved = {};
    const linkedImport = [];
    const linkedExport = [];

    (record.items || []).forEach((item) => {
      const key = itemToSelectedKey(item);
      if (!key) return;
      keys.push(key);
      if (item.approved_amount != null) {
        approved[key] = Number(item.approved_amount);
      }

      const importBill = item.import_cnf_bill || item.importCnfBill;
      const exportBill = item.export_cnf_bill || item.exportCnfBill;

      if (item.bill_type === "import" && importBill) {
        linkedImport.push(importBill);
      }
      if (item.bill_type === "export" && exportBill) {
        linkedExport.push(exportBill);
      }
    });

    setSelectedKeys(keys);
    setApprovedAmounts(approved);
    setImportBills((prev) => mergeUniqueBills(prev, linkedImport));
    setExportBills((prev) => mergeUniqueBills(prev, linkedExport));
  }, []);

  const initialize = useCallback(async () => {
    setLoadingLists(true);
    setFetchError(null);
    if (prepareBillingId) {
      setLoadingRecord(true);
    }

    try {
      const { importRows, exportRows } = await loadAvailableBills();

      if (prepareBillingId) {
        const record = await loadRecord(prepareBillingId);
        const linkedImport = [];
        const linkedExport = [];

        (record?.items || []).forEach((item) => {
          const importBill = item.import_cnf_bill || item.importCnfBill;
          const exportBill = item.export_cnf_bill || item.exportCnfBill;
          if (importBill) linkedImport.push(importBill);
          if (exportBill) linkedExport.push(exportBill);
        });

        setImportBills(mergeUniqueBills(linkedImport, importRows));
        setExportBills(mergeUniqueBills(linkedExport, exportRows));
        applyRecordToState(record);
      } else {
        setImportBills(importRows);
        setExportBills(exportRows);
      }
    } finally {
      setLoadingLists(false);
      setLoadingRecord(false);
    }
  }, [prepareBillingId, loadAvailableBills, loadRecord, applyRecordToState]);

  const fetchNextBillNumber = useCallback(async () => {
    setLoadingBillNumber(true);
    try {
      const response = await getData(CNF_PREPARE_BILLING_NEXT_BILL_NUMBER_ENDPOINT);
      if (response?.status === 200) {
        const generated =
          response.data?.data?.bill_number ?? response.data?.data ?? "";
        setBillNumber(generated ? String(generated) : "");
      } else {
        message.error("Could not generate bill number.");
      }
    } finally {
      setLoadingBillNumber(false);
    }
  }, []);

  useEffect(() => {
    if (editInfoProp) {
      applyRecordToState(editInfoProp);
    }
  }, [editInfoProp, applyRecordToState]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (isAdd) {
      fetchNextBillNumber();
    }
  }, [isAdd, fetchNextBillNumber]);

  const importOptions = useMemo(
    () =>
      importBills.map((row) => ({
        value: `${IMPORT_VAL_PREFIX}${row.id}`,
        label: row.bill_no || `Import #${row.id}`,
      })),
    [importBills],
  );

  const exportOptions = useMemo(
    () =>
      exportBills.map((row) => ({
        value: `${EXPORT_VAL_PREFIX}${row.id}`,
        label: row.bill_no || `Export #${row.id}`,
      })),
    [exportBills],
  );

  const importById = useMemo(() => {
    const m = new Map();
    importBills.forEach((r) => m.set(r.id, r));
    return m;
  }, [importBills]);

  const exportById = useMemo(() => {
    const m = new Map();
    exportBills.forEach((r) => m.set(r.id, r));
    return m;
  }, [exportBills]);

  const tableRows = useMemo(() => {
    const rows = [];
    for (const val of selectedKeys) {
      if (typeof val !== "string") continue;
      if (val.startsWith(IMPORT_VAL_PREFIX)) {
        const id = Number(val.slice(IMPORT_VAL_PREFIX.length));
        const bill = importById.get(id);
        if (!bill) continue;
        const inv = bill.import_invoice;
        rows.push({
          key: val,
          sourceType: "Import",
          factory: inv?.buyer_name || "—",
          billNo: bill.bill_no || bill.id,
          lcNo: formatLcNoImport(inv),
          billOfEntry: bill.bill_of_entry_no || "—",
          beDate: formatShortDate(bill.bill_of_entry_date),
          quantityCtn: formatQuantityImport(inv),
          goods: formatGoodsDescription(inv?.goods_description),
          amountNum: getImportCnfBillRowTotal(bill),
          approvedAmountNum: approvedAmounts[val] ?? getImportCnfBillRowTotal(bill),
          location: inv?.loading_point || "—",
        });
      } else if (val.startsWith(EXPORT_VAL_PREFIX)) {
        const id = Number(val.slice(EXPORT_VAL_PREFIX.length));
        const bill = exportById.get(id);
        if (!bill) continue;
        const inv = bill.export_invoice;
        rows.push({
          key: val,
          sourceType: "Export",
          factory: "—",
          billNo: bill.bill_no || bill.id,
          lcNo: formatLcNoExport(inv),
          billOfEntry: inv?.boe_number || "—",
          beDate: formatShortDate(inv?.boe_date),
          quantityCtn: formatQuantityExport(inv),
          goods: formatGoodsDescription(inv?.goods_description),
          amountNum: getExportCnfBillRowTotal(bill),
          approvedAmountNum: approvedAmounts[val] ?? getExportCnfBillRowTotal(bill),
          location: inv?.origin_port || inv?.destination_country || "—",
        });
      }
    }
    return rows;
  }, [selectedKeys, importById, exportById, approvedAmounts]);

  const grandTotal = useMemo(
    () => tableRows.reduce((s, r) => s + (Number(r.amountNum) || 0), 0),
    [tableRows],
  );

  const approvedGrandTotal = useMemo(
    () => tableRows.reduce((s, r) => s + (Number(r.approvedAmountNum) || 0), 0),
    [tableRows],
  );

  const buildItemsPayload = useCallback(() => {
    const approvedByKey = new Map(tableRows.map((r) => [r.key, r.approvedAmountNum]));
    const items = [];
    for (const val of selectedKeys) {
      if (typeof val !== "string") continue;
      let billType = null;
      let id = NaN;
      if (val.startsWith(IMPORT_VAL_PREFIX)) {
        billType = "import";
        id = Number(val.slice(IMPORT_VAL_PREFIX.length));
      } else if (val.startsWith(EXPORT_VAL_PREFIX)) {
        billType = "export";
        id = Number(val.slice(EXPORT_VAL_PREFIX.length));
      }
      if (billType && !Number.isNaN(id)) {
        items.push({
          bill_type: billType,
          bill_id: id,
          approved_amount: approvedByKey.get(val) ?? null,
        });
      }
    }
    return items;
  }, [selectedKeys, tableRows]);

  const handleSave = async () => {
    const trimmedBillNumber = billNumber?.trim();
    if (isAdd && !trimmedBillNumber) {
      message.error("Bill number is required.");
      return;
    }

    const items = buildItemsPayload();
    if (!items.length) {
      message.error("Select at least one Import or Export C&F bill.");
      return;
    }

    setSaving(true);
    try {
      let response;
      if (isEdit && prepareBillingId && !canUpdate && canEditApprovedAmount) {
        response = await putData(
          `${CNF_PREPARE_BILLING_UPDATE_ENDPOINT}/${prepareBillingId}/approved-amounts`,
          { items },
        );
      } else if (isEdit && prepareBillingId) {
        response = await putData(`${CNF_PREPARE_BILLING_UPDATE_ENDPOINT}/${prepareBillingId}`, {
          items,
        });
      } else {
        response = await postData(CNF_PREPARE_BILLING_STORE_ENDPOINT, {
          bill_number: trimmedBillNumber,
          items,
        });
      }

      if (response && (response?.code === 200 || response?.status === 200)) {
        message.success(
          response?.message ||
          (isEdit
            ? "Prepare billing updated successfully."
            : "Prepare billing created successfully. Send request from the list when ready."),
        );
        navigate(listPath);
      }
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = viewMode
    ? "Prepare billing — View"
    : isEdit
      ? !canUpdate && canEditApprovedAmount
        ? "Prepare billing — Edit Approved Amount"
        : canUpdate
          ? recordStatus === "rejected"
            ? "Prepare billing — Edit & Re-submit"
            : "Prepare billing — Edit"
          : "Prepare billing — View"
      : "Prepare billing — Add";

  const columns = useMemo(
    () => [
      {
        title: "Type",
        dataIndex: "sourceType",
        key: "sourceType",
        width: 88,
        fixed: "left",
        render: (text) => <Input size="small" value={text} disabled />,
      },
      { title: "Factory", dataIndex: "factory", key: "factory", width: 140, ellipsis: true },
      { title: "Bill No", dataIndex: "billNo", key: "billNo", width: 150 },
      { title: "L/C No", dataIndex: "lcNo", key: "lcNo", width: 130, ellipsis: true },
      { title: "Bill of Entry", dataIndex: "billOfEntry", key: "billOfEntry", width: 120, ellipsis: true },
      { title: "B/E Date", dataIndex: "beDate", key: "beDate", width: 100 },
      { title: "Quantity / Ctn", dataIndex: "quantityCtn", key: "quantityCtn", width: 160, ellipsis: true },
      { title: "Goods", dataIndex: "goods", key: "goods", width: 140, ellipsis: true },
      {
        title: "Amount",
        dataIndex: "amountNum",
        key: "amountNum",
        align: "right",
        width: 130,
        render: (v) => formatAmountTaka(v),
      },
      {
        title: "Approved Amount",
        dataIndex: "approvedAmountNum",
        key: "approvedAmountNum",
        align: "right",
        width: 150,
        render: (v, row) =>
          approvedAmountEditable ? (
            <InputNumber
              size="small"
              min={0}
              precision={2}
              style={{ width: "100%" }}
              value={v}
              onChange={(value) =>
                setApprovedAmounts((prev) => ({ ...prev, [row.key]: value ?? 0 }))
              }
            />
          ) : (
            formatAmountTaka(v)
          ),
      },
      { title: "Location", dataIndex: "location", key: "location", width: 100, ellipsis: true },
    ],
    [approvedAmountEditable],
  );

  const pageLoading = loadingLists || loadingRecord || (isAdd && loadingBillNumber);

  return (
    <AppContent
      header={
        <AppPageHeader
          title={
            <Space>
              <Title level={5} style={{ margin: 0 }}>
                {pageTitle}
              </Title>
              {recordStatus && <PrepareBillingStatusTag status={recordStatus} />}
            </Space>
          }
          extra={
            <Space>
              <Button size="small" onClick={() => navigate(listPath)} disabled={saving}>
                {viewMode || (!canUpdate && !canEditApprovedAmount) ? "Back" : "Discard"}
              </Button>
              {(canUpdate || canEditApprovedAmount) && !viewMode && (
                <Button
                  type="primary"
                  size="small"
                  loading={saving}
                  disabled={saving || pageLoading || (isAdd && !billNumber)}
                  onClick={handleSave}
                >
                  {isEdit && !canUpdate
                    ? "Save Approved Amount"
                    : isEdit
                      ? recordStatus === "rejected"
                        ? "Update & Re-submit"
                        : "Update"
                      : "Submit Request"}
                </Button>
              )}
            </Space>
          }
        />
      }
      content={
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Card size="small" title="Select C&F bills" loading={pageLoading}>
            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
              <Col span={6}>
                <Text strong>Bill No</Text>
                <Input
                  size="small"
                  readOnly
                  disabled
                  value={billNumber}
                  placeholder={isAdd ? "Generating…" : ""}
                  style={{ marginTop: 8, width: "100%" }}
                />
                {isAdd && (
                  <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                    System will generate bill number
                  </Text>
                )}
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Text strong>Import C&F Bill Numbers</Text>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select one or more Import C&F bills"
                  style={{ width: "100%", marginTop: 8 }}
                  options={importOptions}
                  disabled={disabled}
                  value={selectedKeys.filter((k) => String(k).startsWith(IMPORT_VAL_PREFIX))}
                  onChange={(vals) => {
                    const rest = selectedKeys.filter(
                      (k) => !String(k).startsWith(IMPORT_VAL_PREFIX),
                    );
                    setSelectedKeys([...rest, ...vals]);
                  }}
                />
              </Col>
              <Col xs={24} lg={12}>
                <Text strong>Export C&F Bill Numbers</Text>
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select Export C&F bills not yet in any prepare billing"
                  style={{ width: "100%", marginTop: 8 }}
                  options={exportOptions}
                  disabled={disabled}
                  value={selectedKeys.filter((k) => String(k).startsWith(EXPORT_VAL_PREFIX))}
                  onChange={(vals) => {
                    const rest = selectedKeys.filter(
                      (k) => !String(k).startsWith(EXPORT_VAL_PREFIX),
                    );
                    setSelectedKeys([...rest, ...vals]);
                  }}
                />
              </Col>
            </Row>
          </Card>

          <Card size="small" title="Selected bills">
            <ListTable
              size="small"
              rowKey="key"
              loading={pageLoading}
              error={fetchError}
              onRetry={() => initialize()}
              columns={columns}
              dataSource={tableRows}
              pagination={false}
              bordered
              scroll={{ x: 1500 }}
              locale={{ emptyText: "Select bills above to build this list." }}
              summary={() =>
                tableRows.length ? (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={8} align="right">
                        <Text strong>Total Taka</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <Text strong>{formatAmountTaka(grandTotal)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right">
                        <Text strong>{formatAmountTaka(approvedGrandTotal)}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} />
                    </Table.Summary.Row>
                  </Table.Summary>
                ) : null
              }
            />
          </Card>
        </Space>
      }
    />
  );
}
